import React, { useState } from 'react';
import '../styles/ScheduleInterview.css';

const ScheduleInterview = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobEmployer: '',
    jobTitle: '',
    date: '',
    time: '',
  });

  const [errors, setErrors] = useState({});

  // Job data
  const companies = [
    { value: 'company1', label: 'Company 1' },
    { value: 'company2', label: 'Company 2' },
    { value: 'company3', label: 'Company 3' },
    { value: 'company4', label: 'Company 4' },
  ];

  const jobPositions = [
    { value: 'frontend', label: 'Frontend Developer' },
    { value: 'backend', label: 'Backend Engineer' },
    { value: 'designer', label: 'UX/UI Designer' },
    { value: 'pm', label: 'Project Manager' },
  ];

  const timeSlots = [
    { id: 'time1', value: '09:00', label: '9:00 AM' },
    { id: 'time2', value: '11:00', label: '11:00 AM' },
    { id: 'time3', value: '14:00', label: '2:00 PM' },
    { id: 'time4', value: '16:00', label: '4:00 PM' },
    { id: 'time5', value: '19:00', label: '7:00 PM' },
    { id: 'time6', value: '20:00', label: '8:00 PM' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.jobEmployer) {
      newErrors.jobEmployer = 'Please select a company';
    }

    if (!formData.jobTitle) {
      newErrors.jobTitle = 'Please select a position';
    }

    if (!formData.date) {
      newErrors.date = 'Please select a date';
    }

    if (!formData.time) {
      newErrors.time = 'Please select a time slot';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Here you would typically send the data to your backend
      console.log('Form submitted:', formData);

      // Show success message or redirect
      alert('Interview scheduled successfully! We will contact you soon.');

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        jobEmployer: '',
        jobTitle: '',
        date: '',
        time: '',
      });
    }
  };

  // Get tomorrow's date for min date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className='container'>
      <header>
        <h1>Interview Scheduling Portal</h1>
        <p>
          Please provide your details and select the available interview slot
        </p>
      </header>

      <main>
        <form className='scheduling-form' onSubmit={handleSubmit}>
          {/* Candidate Information Section */}
          <section className='candidate-info'>
            <h2>Your Information</h2>
            <div className='form-group'>
              <label htmlFor='name'>Full Name *</label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                required
                className={errors.name ? 'error' : ''}
              />
              {errors.name && (
                <span className='error-message'>{errors.name}</span>
              )}
            </div>
            <div className='form-group'>
              <label htmlFor='email'>Email Address *</label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                onChange={handleInputChange}
                required
                className={errors.email ? 'error' : ''}
              />
              {errors.email && (
                <span className='error-message'>{errors.email}</span>
              )}
            </div>
            <div className='form-group'>
              <label htmlFor='phone'>Phone Number</label>
              <input
                type='tel'
                id='phone'
                name='phone'
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
          </section>

          <hr />

          {/* Job Selection Section */}
          <section className='job-selection'>
            <h2>Job Details</h2>
            <div className='form-group'>
              <label htmlFor='jobEmployer'>Applying For *</label>
              <select
                id='jobEmployer'
                name='jobEmployer'
                value={formData.jobEmployer}
                onChange={handleInputChange}
                required
                className={errors.jobEmployer ? 'error' : ''}
              >
                <option value='' disabled>
                  Select a Company
                </option>
                {companies.map((company) => (
                  <option key={company.value} value={company.value}>
                    {company.label}
                  </option>
                ))}
              </select>
              {errors.jobEmployer && (
                <span className='error-message'>{errors.jobEmployer}</span>
              )}
            </div>
            <div className='form-group'>
              <label htmlFor='jobTitle'>Available Jobs *</label>
              <select
                id='jobTitle'
                name='jobTitle'
                value={formData.jobTitle}
                onChange={handleInputChange}
                required
                className={errors.jobTitle ? 'error' : ''}
              >
                <option value='' disabled>
                  Select a Position
                </option>
                {jobPositions.map((job) => (
                  <option key={job.value} value={job.value}>
                    {job.label}
                  </option>
                ))}
              </select>
              {errors.jobTitle && (
                <span className='error-message'>{errors.jobTitle}</span>
              )}
            </div>
          </section>

          <hr />

          {/* Time Selection Section */}
          <section className='time-selection'>
            <h2>Preferred Time Slot</h2>
            <p className='instruction'>
              Select the day and a time that works best for you from the
              available slots
            </p>

            <div className='calendar-display'>
              <div className='date-selector'>
                <label htmlFor='date'>Select Date *</label>
                <input
                  type='date'
                  id='date'
                  name='date'
                  value={formData.date}
                  onChange={handleInputChange}
                  min={getTomorrowDate()}
                  required
                  className={errors.date ? 'error' : ''}
                />
                {errors.date && (
                  <span className='error-message'>{errors.date}</span>
                )}
              </div>

              <div className='time-options'>
                <label>Select Time *</label>
                <div className='time-grid'>
                  {timeSlots.map((slot) => (
                    <React.Fragment key={slot.id}>
                      <input
                        type='radio'
                        id={slot.id}
                        name='time'
                        value={slot.value}
                        checked={formData.time === slot.value}
                        onChange={handleInputChange}
                        required
                      />
                      <label htmlFor={slot.id}>{slot.label}</label>
                    </React.Fragment>
                  ))}
                </div>
                {errors.time && (
                  <span className='error-message'>{errors.time}</span>
                )}
              </div>
            </div>
          </section>

          <button type='submit' className='submit-btn'>
            Confirm Appointment
          </button>
        </form>
      </main>
    </div>
  );
};

export default ScheduleInterview;
